import {registerGraphNode} from "#graph/decorators/register-node"
import type {Evaluator} from "#graph/evaluators/types"
import type {ParameterValue} from "#graph/nodes/graph/helpers"
import {GraphNode} from "#graph/nodes/graph/graph-node"
import type {ListParameters} from "#graph/nodes/list/definition"
import {listNodeDefinition} from "#graph/nodes/list/definition"
import {lazyDefinition} from "#graph/helpers/lazy-definition"
import {z} from "zod/v4-mini"

const numberListDefinition = lazyDefinition(() => listNodeDefinition<number>("NumberList", z.number()))
const stringListDefinition = lazyDefinition(() => listNodeDefinition<string>("StringList", z.string()))

export abstract class List<ItemType, Context = unknown> extends GraphNode<ItemType[], Context, ListParameters<ItemType>> {
    override run = async (evaluator: Evaluator<Context>): Promise<ItemType[]> => {
        return Promise.all(this.parameters.list.map((item) => evaluator.get(item)))
    }

    override runSync = (evaluator: Evaluator<Context>): ItemType[] => {
        return this.parameters.list.map((item) => evaluator.getSync(item))
    }

    addEntry(entry: ParameterValue<ItemType>) {
        const newList = [...this.parameters.list, entry]
        this.replaceParameters({list: newList})
    }

    removeEntry(entry: ParameterValue<ItemType>) {
        const {list} = this.parameters

        const index = list.indexOf(entry)
        if (index === -1) return

        const newList = [...list]
        newList.splice(index, 1)
        this.replaceParameters({list: newList})
    }

    clear() {
        this.replaceParameters({list: []})
    }
}

@registerGraphNode
export class NumberList<Context = unknown> extends List<number, Context> {
    public override get definition() {
        return numberListDefinition()
    }
}

@registerGraphNode
export class StringList<Context = unknown> extends List<string, Context> {
    public override get definition() {
        return stringListDefinition()
    }
}
